import { api } from "@/app/services/api.service";

export const HELPDESK_TICKET_LOGS_PATH = "integration/api/helpdesk/tickets/logs";

export interface HelpDeskTicketLogUser {
  id: string | null;
  fullName: string | null;
  email: string | null;
  accountType: string | null;
  phoneNumber: string | null;
}

export interface HelpDeskTicketLog {
  id: string;
  subject: string | null;
  description: string | null;
  status: string | null;
  user: HelpDeskTicketLogUser | null;
  httpMethod: string | null;
  outgoingUrl: string | null;
  responseStatus: number | null;
  errorMessage: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  raw: Record<string, unknown>;
}

export interface HelpDeskRetriggerResult {
  status: string | null;
  responseStatus: number | null;
  message: string | null;
  errorMessage: string | null;
}

export interface HelpDeskRetriggerToast {
  type: "success" | "warning";
  title: string;
  text: string;
}

export function extractErrorMessage(error: unknown, fallback: string) {
  const err = error as { response?: { data?: { message?: string } } };
  return err.response?.data?.message || fallback;
}

function unwrapPayload(res: { data: unknown }) {
  const data = res.data as Record<string, unknown>;
  if (
    data != null &&
    typeof data === "object" &&
    Object.prototype.hasOwnProperty.call(data, "data") &&
    data.data != null
  ) {
    return data.data;
  }
  return data;
}

function readString(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value != null && value !== "") return String(value);
  }
  return null;
}

function parseJsonRecord(value: unknown): Record<string, unknown> | null {
  if (value == null) return null;
  if (typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== "string" || value.trim() === "") return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (parsed != null && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return null;
  }
  return null;
}

function parseRequestPayload(value: unknown) {
  const record = parseJsonRecord(value);
  if (!record) return { subject: null, description: null };
  return {
    subject: readString(record, ["subject", "title"]),
    description: readString(record, ["description", "body", "message"]),
  };
}

function parseResponseError(value: unknown) {
  const record = parseJsonRecord(value);
  if (!record) {
    if (typeof value === "string" && value.trim() !== "") return value.trim();
    return null;
  }
  return (
    readString(record, ["error_human", "errorHuman", "message", "error"]) ||
    readString(record, ["detail", "title"])
  );
}

function parseUser(value: unknown): HelpDeskTicketLogUser | null {
  const record = parseJsonRecord(value);
  if (!record) return null;

  const firstName = readString(record, ["firstName", "first_name"]);
  const lastName = readString(record, ["lastName", "last_name"]);
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim() || null;

  return {
    id: readString(record, ["id"]),
    fullName,
    email: readString(record, ["email"]),
    accountType: readString(record, ["accountType", "account_type"]),
    phoneNumber: readString(record, ["phoneNumber", "phone_number"]),
  };
}

function readResponseStatus(record: Record<string, unknown>) {
  const value = record.responseStatus ?? record.response_status ?? record.httpStatus;
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeTicketLog(item: unknown): HelpDeskTicketLog | null {
  if (item == null || typeof item !== "object") return null;
  const record = item as Record<string, unknown>;
  const id = readString(record, ["id", "logId", "log_id"]);
  if (!id) return null;

  const request = parseRequestPayload(record.requestPayload);
  const user = parseUser(record.user);

  return {
    id,
    subject: request.subject,
    description: request.description,
    status: readString(record, ["status", "state", "submissionStatus"]),
    user,
    httpMethod: readString(record, ["httpMethod", "http_method", "method"]),
    outgoingUrl: readString(record, ["outgoingUrl", "outgoing_url", "url"]),
    responseStatus: readResponseStatus(record),
    errorMessage: parseResponseError(record.responseBody),
    createdAt: readString(record, ["createdAt", "created_at", "timestamp", "submittedAt"]),
    updatedAt: readString(record, ["updatedAt", "updated_at"]),
    raw: record,
  };
}

function normalizeRecord(body: unknown): Record<string, unknown> | null {
  if (body == null) return null;
  if (typeof body === "object" && !Array.isArray(body)) {
    return body as Record<string, unknown>;
  }
  return parseJsonRecord(body);
}

export function parseRetriggerResponse(body: unknown): HelpDeskRetriggerResult {
  const record = normalizeRecord(body);
  if (!record) {
    return {
      status: null,
      responseStatus: null,
      message: null,
      errorMessage: null,
    };
  }

  return {
    status: readString(record, ["status", "state"]),
    responseStatus: readResponseStatus(record),
    message: readString(record, ["message"]),
    errorMessage: parseResponseError(record.responseBody),
  };
}

export function buildRetriggerToast(result: HelpDeskRetriggerResult): HelpDeskRetriggerToast {
  const title = result.message || "Retrigger completed";
  const failed = (result.status || "").toUpperCase() === "FAILED";

  if (failed) {
    const parts: string[] = [];
    if (result.errorMessage) parts.push(result.errorMessage);
    if (result.responseStatus != null) parts.push(`HTTP ${result.responseStatus}`);
    if (result.status) parts.push(`Status: ${result.status}`);
    return {
      type: "warning",
      title,
      text: parts.length > 0 ? parts.join(" · ") : "Retrigger completed but submission failed",
    };
  }

  return {
    type: "success",
    title,
    text: result.errorMessage || "Ticket submission retriggered successfully",
  };
}

export function extractTicketLogList(body: unknown): HelpDeskTicketLog[] {
  if (body == null) return [];
  const list = Array.isArray(body)
    ? body
    : (() => {
        const obj = body as Record<string, unknown>;
        const candidates = [obj.logs, obj.ticketLogs, obj.items, obj.content, obj.records, obj.data];
        for (const candidate of candidates) {
          if (Array.isArray(candidate)) return candidate;
        }
        return [];
      })();

  return list
    .map((item) => normalizeTicketLog(item))
    .filter((item): item is HelpDeskTicketLog => item != null);
}

export async function fetchHelpDeskTicketLogs() {
  const res = await api().get(HELPDESK_TICKET_LOGS_PATH);
  const root = res.data;
  if (Array.isArray(root)) return extractTicketLogList(root);
  if (root != null && typeof root === "object") {
    const rootObj = root as Record<string, unknown>;
    if (Array.isArray(rootObj.data)) return extractTicketLogList(rootObj.data);
    const unwrapped = unwrapPayload(res);
    const fromUnwrapped = extractTicketLogList(unwrapped);
    if (fromUnwrapped.length > 0) return fromUnwrapped;
    return extractTicketLogList(root);
  }
  return [];
}

export async function retriggerHelpDeskTicketLog(logId: string) {
  const path = `${HELPDESK_TICKET_LOGS_PATH}/${encodeURIComponent(logId)}/retrigger`;
  const res = await api().post(path);
  const root = res.data;
  if (root != null && typeof root === "object") {
    const rootObj = root as Record<string, unknown>;
    if (rootObj.data != null && typeof rootObj.data === "object") {
      return parseRetriggerResponse(rootObj.data);
    }
    return parseRetriggerResponse(root);
  }
  return parseRetriggerResponse(root);
}
